import { useState } from 'react';
import { Loader2, ShieldAlert, X } from 'lucide-react';
import { useGetEmployeeReportsQuery, useUpdateEmployeeReportStatusMutation } from '@/store/api/apiSlice';
import { parseErrorMessage } from '@/utils/parseErrorMessage';
import { CATEGORY_LABELS, SEVERITY_PILL, STATUS_LABELS, STATUS_PILL } from '@/utils/employeeReports';
import type {
    EmployeeReport,
    EmployeeReportListParams,
    EmployeeReportStatus,
    UpdateEmployeeReportStatusRequest,
} from '@/types';

const PAGE_SIZE = 20;

const STATUS_FILTERS: { value: '' | EmployeeReportStatus; label: string }[] = [
    { value: '', label: 'All statuses' },
    { value: 'active', label: 'Active' },
    { value: 'dismissed', label: 'Dismissed' },
];

function formatDate(value?: string): string {
    if (!value) return '';
    const d = new Date(value);
    if (isNaN(d.getTime())) return '';
    return d.toLocaleDateString('en-GB', { day: 'numeric', month: 'short', year: 'numeric' });
}

function contactLine(r: EmployeeReport): string {
    return [r.employeeEmail, r.employeePhone, r.employeeNin ? `NIN ${r.employeeNin}` : ''].filter(Boolean).join(' · ');
}

export default function AdminEmployeeReports() {
    const [status, setStatus] = useState<'' | EmployeeReportStatus>('active');
    const [page, setPage] = useState(1);
    const [target, setTarget] = useState<{ report: EmployeeReport; next: EmployeeReportStatus } | null>(null);

    const listArgs: EmployeeReportListParams = { page, limit: PAGE_SIZE };
    if (status) listArgs.status = status;
    const { data, isLoading, isFetching } = useGetEmployeeReportsQuery(listArgs);

    const reports = data?.data ?? [];
    const pagination = data?.pagination;

    return (
        <main className="w-full min-h-screen flex flex-col px-4 py-6 md:px-6 lg:px-8 xl:px-12 bg-[#EDEFF3]">
            <div className="bg-white rounded-3xl shadow-sm flex-1 flex flex-col overflow-hidden">
                <div className="flex-1 overflow-y-auto p-4 md:p-8 lg:p-10 flex flex-col gap-6 w-full">
                    <div className="flex flex-wrap items-center justify-between gap-3">
                        <div className="flex items-center gap-2">
                            <ShieldAlert className="h-6 w-6 text-[#FF6B35]" />
                            <h1 className="text-2xl md:text-3xl font-bold text-gray-900">Employee reports</h1>
                        </div>
                        <select
                            value={status}
                            onChange={(e) => {
                                setStatus(e.target.value as '' | EmployeeReportStatus);
                                setPage(1);
                            }}
                            className="rounded-lg border border-[#e0e0e0] bg-white px-3 py-2 text-sm outline-none focus:border-[#FF6B35]"
                        >
                            {STATUS_FILTERS.map((o) => (
                                <option key={o.value} value={o.value}>
                                    {o.label}
                                </option>
                            ))}
                        </select>
                    </div>

                    {isLoading ? (
                        <div className="flex justify-center py-16">
                            <Loader2 className="h-6 w-6 animate-spin text-[#FF6B35]" />
                        </div>
                    ) : reports.length === 0 ? (
                        <p className="py-12 text-center text-sm text-[#9aa0a6]">No reports found.</p>
                    ) : (
                        <ul className="flex flex-col gap-3">
                            {reports.map((r) => (
                                <li key={r.id} className="rounded-xl border border-[#f0f0f0] p-4">
                                    <div className="flex flex-wrap items-start justify-between gap-3">
                                        <div className="min-w-0 flex-1">
                                            <div className="mb-1 flex flex-wrap items-center gap-2">
                                                <span className="text-sm font-semibold text-[#1b1b1b]">
                                                    {r.employeeFullName}
                                                </span>
                                                <span className="text-xs text-[#717680]">
                                                    {CATEGORY_LABELS[r.category] ?? r.category}
                                                </span>
                                                <span
                                                    className={`inline-block rounded-md border px-1.5 py-0.5 text-[10px] font-semibold uppercase tracking-wide ${SEVERITY_PILL[r.severity]}`}
                                                >
                                                    {r.severity}
                                                </span>
                                                <span
                                                    className={`inline-block rounded-md border px-1.5 py-0.5 text-[10px] font-semibold ${STATUS_PILL[r.status]}`}
                                                >
                                                    {STATUS_LABELS[r.status] ?? r.status}
                                                </span>
                                            </div>
                                            {contactLine(r) && (
                                                <p className="text-xs text-[#717680]">{contactLine(r)}</p>
                                            )}
                                            <p className="mt-2 text-sm text-[#4e4d4d] whitespace-pre-wrap">
                                                {r.description}
                                            </p>
                                            {r.reviewNotes && (
                                                <p className="mt-2 rounded-lg bg-[#fafafa] px-3 py-2 text-xs text-[#4e4d4d]">
                                                    <span className="font-semibold">Review notes:</span> {r.reviewNotes}
                                                </p>
                                            )}
                                        </div>
                                        <div className="flex shrink-0 flex-col items-end gap-2">
                                            <span className="text-xs text-[#717680]">{formatDate(r.createdAt)}</span>
                                            {r.status === 'active' ? (
                                                <button
                                                    type="button"
                                                    onClick={() => setTarget({ report: r, next: 'dismissed' })}
                                                    className="rounded-lg border border-[#e0e0e0] px-3 py-1.5 text-xs font-medium text-[#1b1b1b] hover:bg-gray-50"
                                                >
                                                    Dismiss
                                                </button>
                                            ) : (
                                                <button
                                                    type="button"
                                                    onClick={() => setTarget({ report: r, next: 'active' })}
                                                    className="rounded-lg border border-green-200 px-3 py-1.5 text-xs font-medium text-green-700 hover:bg-green-50"
                                                >
                                                    Reactivate
                                                </button>
                                            )}
                                        </div>
                                    </div>
                                </li>
                            ))}
                        </ul>
                    )}

                    {pagination && pagination.totalPages > 1 && (
                        <div className="flex items-center justify-between gap-3 pt-2">
                            <p className="text-xs text-[#717680]">
                                Page {pagination.page} of {pagination.totalPages} · {pagination.total} total
                            </p>
                            <div className="flex gap-2">
                                <button
                                    type="button"
                                    onClick={() => setPage((p) => Math.max(1, p - 1))}
                                    disabled={page <= 1 || isFetching}
                                    className="rounded-lg border border-[#e0e0e0] px-3 py-1.5 text-xs font-medium disabled:opacity-50"
                                >
                                    Previous
                                </button>
                                <button
                                    type="button"
                                    onClick={() => setPage((p) => Math.min(pagination.totalPages, p + 1))}
                                    disabled={page >= pagination.totalPages || isFetching}
                                    className="rounded-lg border border-[#e0e0e0] px-3 py-1.5 text-xs font-medium disabled:opacity-50"
                                >
                                    Next
                                </button>
                            </div>
                        </div>
                    )}
                </div>
            </div>

            {target && <StatusModal target={target} onClose={() => setTarget(null)} />}
        </main>
    );
}

function StatusModal({
    target,
    onClose,
}: {
    target: { report: EmployeeReport; next: EmployeeReportStatus };
    onClose: () => void;
}) {
    const { report, next } = target;
    const [notes, setNotes] = useState(report.reviewNotes ?? '');
    const [error, setError] = useState('');
    const [update, { isLoading }] = useUpdateEmployeeReportStatusMutation();

    const dismissing = next === 'dismissed';

    const submit = async () => {
        setError('');
        try {
            const payload: UpdateEmployeeReportStatusRequest = { id: report.id, status: next };
            if (notes.trim()) payload.reviewNotes = notes.trim();
            await update(payload).unwrap();
            onClose();
        } catch (err) {
            setError(parseErrorMessage(err, 'Failed to update status.'));
        }
    };

    return (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 px-4">
            <div className="w-full max-w-md rounded-2xl bg-white p-5 shadow-xl">
                <div className="mb-3 flex items-center justify-between">
                    <h3 className="text-base font-bold text-[#151515]">
                        {dismissing ? 'Dismiss report' : 'Reactivate report'}
                    </h3>
                    <button type="button" onClick={onClose} className="text-[#717680] hover:text-[#151515]">
                        <X className="h-5 w-5" />
                    </button>
                </div>
                <p className="mb-3 text-sm text-[#4e4d4d]">
                    {dismissing
                        ? `Mark the report on ${report.employeeFullName} as dismissed. Add a note explaining why.`
                        : `Reactivate the report on ${report.employeeFullName}.`}
                </p>
                <label className="mb-1 block text-sm font-medium text-[#1b1b1b]">
                    Review notes{dismissing ? '' : ' (optional)'}
                </label>
                <textarea
                    value={notes}
                    onChange={(e) => setNotes(e.target.value)}
                    rows={3}
                    className="w-full rounded-lg border border-[#e0e0e0] bg-white px-3 py-2.5 text-sm outline-none focus:border-[#FF6B35] focus:ring-2 focus:ring-[#FF6B35]/15"
                    placeholder="Reason / context…"
                />
                {error && <p className="mt-2 text-xs text-red-600">{error}</p>}
                <div className="mt-4 flex justify-end gap-2">
                    <button
                        type="button"
                        onClick={onClose}
                        className="rounded-lg border border-[#e0e0e0] px-4 py-2 text-sm font-medium text-[#1b1b1b] hover:bg-gray-50"
                    >
                        Cancel
                    </button>
                    <button
                        type="button"
                        onClick={submit}
                        disabled={isLoading}
                        className="inline-flex items-center gap-2 rounded-lg bg-[#151515] px-4 py-2 text-sm font-medium text-white hover:bg-[#2a2a2a] disabled:opacity-60"
                    >
                        {isLoading && <Loader2 className="h-4 w-4 animate-spin" />}
                        {dismissing ? 'Dismiss' : 'Reactivate'}
                    </button>
                </div>
            </div>
        </div>
    );
}
